import React, { useEffect, useRef, useState } from "react";

import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Paper,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    useMediaQuery,
    Theme,
    Drawer,
    CircularProgress,
} from "@mui/material";
import AlarmOutlinedIcon from '@mui/icons-material/AlarmOutlined';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Worker, Viewer, ScrollMode } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { PINNACLE_LOGO_URL } from "../../utils/constants";
import { initiateExam, submitExam } from "../../api/services/exam";
import { handleApiError } from "../../utils/errorHandler";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { ExamAttemptDetails, ExamListByCode, InitiateExam, InitiateExamData } from "../../api/interfaces/Exam";
import { PinnacleDialog } from "../../components/PinnacleDialog";

const APP_BAR_HEIGHT = 64;
const PDFJS_WORKER_VERSION = "3.11.174";

const TakeExam: React.FC = () => {
    const { examId } = useParams<{ examId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const prevLocation = useRef(location);
    const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [exam, setExam] = useState<ExamListByCode | null>(null);
    const [attempt, setAttempt] = useState<InitiateExamData | null>(null);
    const [answers, setAnswers] = React.useState<Record<number, string>>({});

    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const [showSubmitDialog, setShowSubmitDialog] = React.useState(false);

    const [showLeaveDialog, setShowLeaveDialog] = React.useState(false);
    const pendingNavigation = React.useRef<(() => void) | null>(null);

    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

    const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

    const hasInitiatedExam = React.useRef(false);

    useEffect(() => {
        const init = async () => {
            if (!examId) return;
            if (hasInitiatedExam.current) return;
            hasInitiatedExam.current = true;

            try {
                setIsLoading(true);
                const startExam = await initiateExam(examId);
                setExam(startExam.data.exam);
                setAttempt(startExam.data.attempt);
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, [examId]);

    useEffect(() => {
        if (exam?.timer) {
            setRemainingSeconds(exam.timer * 60);
        }
    }, [exam]);

    useEffect(() => {
        if (remainingSeconds === null) return;

        if (remainingSeconds <= 0) {
            handleSubmit(); // auto submit when timer ends
            return;
        }

        const intervalId = setInterval(() => {
            setRemainingSeconds(prev => (prev !== null ? prev - 1 : null));
        }, 1000);

        return () => clearInterval(intervalId);
    }, [remainingSeconds]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            // 1. Warn user (native browser dialog)
            event.preventDefault();
            event.returnValue = "";

            // 2. Auto-submit silently
            handleSubmit()
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [answers]);

    useEffect(() => {
        if (location.key !== prevLocation.current.key) {
            // user pressed back/forward
            pendingNavigation.current = () => navigate(location.pathname, { replace: true });
            setShowLeaveDialog(true);
        }
        prevLocation.current = location;
    }, [location, navigate]);

    const formatTime = (totalSeconds: number) => {
        const hours = Math.floor(totalSeconds / 3600)
            .toString()
            .padStart(2, '0');
        const minutes = Math.floor((totalSeconds % 3600) / 60)
            .toString()
            .padStart(2, '0');
        const seconds = (totalSeconds % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const handleSubmit = async () => {
        try {
            if (examId && exam && attempt) {
                setIsLoading(true);
                const payload = {
                    examAttemptId: attempt._id,
                    answers: Array.from({ length: exam.numberOfQuestions }).map((_, index) => {
                        const questionNumber = index + 1;
                        return answers[questionNumber] ?? "";
                    }),
                };
                const doSubmitExam = await submitExam(examId, payload);
                navigate(`/exam/${doSubmitExam.data._id}/submit`);
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress color="primary" />
                </Box>
            )}
            {/* MOBILE APP BAR */}
            {isSmall && (
                <AppBar position="static" color="default" elevation={1} sx={{ backgroundColor: "#333533" }}>
                    <Toolbar sx={{ justifyContent: "space-between" }}>
                        {/* Back button + title */}
                        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1, overflow: "hidden" }}>
                            <Button
                                sx={{ background: 'transparent', color: 'white', minWidth: 'auto', padding: 0 }}
                                onClick={() => {
                                    pendingNavigation.current = () => navigate(-1);
                                    setShowLeaveDialog(true);
                                }}
                                aria-label="Back"
                            >
                                <KeyboardBackspaceOutlinedIcon />
                            </Button>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: "#fff",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    ml: 1,
                                    maxWidth: 'calc(100% - 40px)',
                                }}
                            >
                                {exam?.name}
                            </Typography>
                        </Box>

                        {/* Answers Key button: prevent stretching */}
                        <Box sx={{ flexShrink: 0, ml: 1 }}>
                            <Button
                                variant="contained"
                                sx={{ bgcolor: "#5840BA", minWidth: 'auto', px: 2 }}
                                startIcon={<InsertDriveFileOutlinedIcon />}
                                onClick={toggleDrawer(true)}
                            >
                                Answers Key
                            </Button>
                        </Box>
                    </Toolbar>
                </AppBar>
            )}

            {/* MAIN CONTENT */}
            <Box
                sx={{
                    display: "flex",
                    height: "100vh",
                    overflow: "hidden",
                }}
            >
                {/* LEFT COLUMN: PDF */}
                <Box
                    component="section"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: { xs: 1, md: 2 },
                        minWidth: 0,
                    }}
                >
                        {!isSmall && (
                        <AppBar position="static" color="default" elevation={1} sx={{ backgroundColor: "#333533" }}>
                            <Toolbar sx={{ justifyContent: "left" }}>
                                <Button
                                    sx={{ background: 'transparent', color: 'white', minWidth: 'auto', padding: 0 }}
                                    aria-label="Back"
                                    onClick={() => {
                                        pendingNavigation.current = () => navigate(-1);
                                        setShowLeaveDialog(true);
                                    }}
                                >
                                    <KeyboardBackspaceOutlinedIcon />
                                </Button>
                                &nbsp;
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff' }}>
                                    {exam?.name}
                                </Typography>
                            </Toolbar>
                        </AppBar>
                    )}

                    <Box
                        sx={{
                            height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
                            minHeight: 0,
                            overflow: "hidden",
                        }}
                    >
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${PDFJS_WORKER_VERSION}/build/pdf.worker.min.js`}>
                            <Box sx={{ height: "100%", overflowY: "auto" }}>
                                {exam && exam.questionnaire && (
                                    <Viewer
                                        fileUrl={exam.questionnaire}
                                        scrollMode={ScrollMode.Vertical}
                                    />
                                )}
                            </Box>
                        </Worker>
                    </Box>
                </Box>

                {/* RIGHT COLUMN: Desktop only */}
                {!isSmall && (
                    <Box
                        sx={{
                            position: "sticky",
                            top: 0,
                            height: "100vh",
                            width: "35%",
                            minWidth: 0,
                            flexShrink: 0,
                        }}
                    >
                        <Paper
                            sx={{
                                p: 3,
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                overflow: "hidden",
                            }}
                        >
                            {/* Top: Logo + Attempt No + Timer */}
                            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                <Box component="img" src={PINNACLE_LOGO_URL} alt="Logo" sx={{ height: 40 }} />
                                {exam && (
                                    <Typography sx={{ alignContent: 'flex-end', color: "#5840BA", fontSize: "12px", fontWeight: 500 }}>
                                        Attempt No: {attempt?.attemptNumber} of {exam?.maxAttempts}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                {exam && (
                                    <>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }} >Answers Key</Typography>
                                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                            <AlarmOutlinedIcon sx={{ color: "#818181" }} />
                                            <Typography variant="body2" sx={{ fontWeight: 700, alignSelf: "center" }} >
                                                {remainingSeconds !== null ? formatTime(remainingSeconds) : '--:--:--'}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>

                            {/* Scrollable questions */}
                            <Box sx={{ flex: 1, overflowY: "auto", pr: 1, minHeight: 0 }}>
                                {exam && exam.numberOfQuestions && (
                                    <Questions
                                        numQuestions={exam?.numberOfQuestions}
                                        answers={answers}
                                        setAnswers={setAnswers}
                                    />
                                )}
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                sx={{
                                    mt: 2,
                                    bgcolor: "#5840BA",
                                    ":hover": {
                                        bgcolor: "#5e35b1"
                                    }
                                }}
                                onClick={() => setShowSubmitDialog(true)}
                            >
                                Submit Exam
                            </Button>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* MOBILE DRAWER */}
            {isSmall && (
                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={toggleDrawer(false)}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    PaperProps={{
                        sx: {
                            top: '56px',
                            height: `calc(100% - 56px)`,
                            width: '90vw',
                            maxWidth: 360,
                        },
                    }}
                >
                    <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
                        <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                            <Button
                                sx={{ minWidth: 'auto', padding: 0 }}
                                onClick={() => {
                                    pendingNavigation.current = () => navigate(-1);
                                    setShowLeaveDialog(true);
                                }}
                                aria-label="Back"
                            >
                                <KeyboardBackspaceOutlinedIcon sx={{ color: "#373737" }} />
                            </Button>
                            <Box component="img" src={PINNACLE_LOGO_URL} alt="Logo" sx={{ height: 25 }} />
                            <Typography sx={{ alignContent: 'center', color: "#5840BA", fontSize: "12px", fontWeight: 500 }}>
                                Attempt No: {attempt?.attemptNumber} of {exam?.maxAttempts}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }} >Answers Key</Typography>
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                <AlarmOutlinedIcon sx={{ color: "#818181" }} />

                                <Typography variant="body2" sx={{ fontWeight: 700, alignSelf: "center" }} >
                                    {remainingSeconds !== null ? formatTime(remainingSeconds) : '--:--:--'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, overflowY: "auto", pr: 1, minHeight: 0 }}>
                            {exam && exam.numberOfQuestions && (
                                <Questions
                                    numQuestions={exam?.numberOfQuestions}
                                    answers={answers}
                                    setAnswers={setAnswers}
                                />
                            )}
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{
                                    mt: 2,
                                    bgcolor: "#5840BA",
                                    ":hover": {
                                        bgcolor: "#5e35b1"
                                    }
                                }}
                                onClick={() => setShowSubmitDialog(true)}
                            >
                                Submit Exam
                            </Button>
                        </Box>
                    </Box>
                </Drawer>
            )}

            {/* LEAVE EXAM */}
            <PinnacleDialog
                isOpen={showLeaveDialog}
                setIsOpen={setShowLeaveDialog}
                headerTitle="Leave Exam?"
                dialogSize="small"
            >

                <Typography>
                    You are about to leave this exam.
                    <br />
                    Your answers will be automatically submitted.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                        fullWidth
                        onClick={() => setShowLeaveDialog(false)}
                        sx={{
                            bgcolor: "#5840BA",
                            color: "#FFFFFF",
                            ":hover": { bgcolor: "#5e35b1" },
                        }}
                    >
                        Stay
                    </Button>

                    <Button
                        fullWidth
                        color="error"
                        variant="contained"
                        onClick={() => {
                            handleSubmit();
                            setShowLeaveDialog(false);
                            pendingNavigation.current?.();
                        }}
                    >
                        Leave Exam
                    </Button>
                </Box>
            </PinnacleDialog>
            <PinnacleDialog
                isOpen={showSubmitDialog}
                setIsOpen={setShowSubmitDialog}
                headerTitle="Submit Exam"
                dialogSize="small"
            >
                <Typography sx={{ color: "#171717", fontSize: "16px", fontWeight: 400, mb: 3 }}>
                    Are you sure you want to submit your exam?
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                        fullWidth
                        sx={{
                            bgcolor: "#5840BA",
                            color: "#FFFFFF",
                            ":hover": { bgcolor: "#5e35b1" },
                        }}
                        onClick={() => {
                            handleSubmit();
                            setShowSubmitDialog(false);
                        }}
                    >
                        Submit Exam
                    </Button>
                    <Button
                        fullWidth
                        sx={{
                            border: "1px solid #D8D8DD",
                            bgcolor: "#fff",
                            color: "#373737",
                            ":hover": { bgcolor: "#CFCFD3" },
                        }}
                        onClick={() => setShowSubmitDialog(false)}
                    >
                        Cancel
                    </Button>
                </Box>
            </PinnacleDialog>

        </>
    );
};

interface QuestionsProps {
    numQuestions: number;
    answers: Record<number, string>;
    setAnswers: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

const Questions: React.FC<QuestionsProps> = ({
    numQuestions,
    answers,
    setAnswers,
}) => {
    const handleChange = (questionNumber: number, value: string) => {
        setAnswers(prev => ({
            ...prev,
            [questionNumber]: value.toUpperCase(),
        }));
    };
    return (
        <>
            {Array.from({ length: numQuestions }).map((_, qIndex) => {
                const qNum = qIndex + 1;

                return (
                    <Box key={qNum} sx={{ mb: 2 }}>
                        <RadioGroup
                            name={`q-${qNum}`}
                            value={answers[qNum] ?? ""}
                            onChange={(_, value) => handleChange(qNum, value)}
                        >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body1" fontWeight={700} sx={{ minWidth: 32 }}>
                                    {qNum}.
                                </Typography>

                                <FormControlLabel
                                    value="A"
                                    control={<Radio size="small" />}
                                    label="a."
                                />
                            </Box>

                            {["B", "C", "D"].map((choice) => (
                                <Box
                                    key={choice}
                                    sx={{ display: "flex", alignItems: "center", ml: "32px" }}
                                >
                                    <FormControlLabel
                                        value={choice}
                                        control={<Radio size="small" />}
                                        label={`${choice.toLowerCase()}.`}
                                    />
                                </Box>
                            ))}
                        </RadioGroup>
                    </Box>
                );
            })}
        </>
    );
};


export default TakeExam;
